/**

 * Exportación reutilizable de listados a CSV y Excel.

 */



import { etiquetaClaseFondo, textoOrigenDestino } from './fondosLabels';
import { textoAclaracionMovimiento } from '../components/MovimientoConceptoCell';
import { buildPrintHeaderHtml, estilosPrintHeader, getPrintLogoUrl, getPrintEncabezado } from './printBranding';
import { fechaHoySistema, formatFechaSistema } from './fechasSistema';

export function fechaArchivo() {
  return fechaHoySistema();
}



export function sanitizeFilename(name) {

  return String(name || 'listado')

    .replace(/[^\w\s\-áéíóúñÁÉÍÓÚÑ]/g, '')

    .replace(/\s+/g, '_')

    .slice(0, 80) || 'listado';

}



export function fmtNumExport(n) {

  return (parseFloat(n) || 0).toFixed(2);

}



export function downloadCsv(filename, headers, rows) {

  const csv = [headers, ...rows]

    .map((row) => row.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))

    .join('\n');

  const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8;' });

  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');

  a.href = url;

  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;

  a.click();

  URL.revokeObjectURL(url);

}



export async function downloadExcel(filename, sheetName, headers, rows) {

  const XLSX = await import('xlsx');

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  const wb = XLSX.utils.book_new();

  const sheet = (sheetName || 'Datos').slice(0, 31);

  XLSX.utils.book_append_sheet(wb, ws, sheet);

  XLSX.writeFile(wb, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);

}



export function exportListado({ filename, sheetName, headers, rows, format }) {

  const base = sanitizeFilename(filename);

  if (format === 'csv') {

    downloadCsv(`${base}_${fechaArchivo()}.csv`, headers, rows);

    return Promise.resolve();

  }

  return downloadExcel(`${base}_${fechaArchivo()}.xlsx`, sheetName || base, headers, rows);

}



export function buildExportRows(items, visibleColumnIds, columnDefs, getCellValue) {

  const headers = visibleColumnIds.map((id) => columnDefs.find((c) => c.id === id)?.label || id);

  const rows = (items || []).map((item) => visibleColumnIds.map((id) => getCellValue(item, id)));

  return { headers, rows };

}



export async function exportFromColumnas({

  filename,

  sheetName,

  columnDefs,

  visibleColumnIds,

  items,

  getCellValue,

  format,

}) {

  if (!items?.length) {

    throw new Error('No hay datos para exportar');

  }

  const { headers, rows } = buildExportRows(items, visibleColumnIds, columnDefs, getCellValue);

  return exportListado({ filename, sheetName, headers, rows, format });

}



function medioPagoFondoLabel(mf) {

  if (mf.forma_pago_nombre) return mf.forma_pago_nombre;

  const detalles = mf.detalles || [];

  if (!detalles.length) return '-';

  const tipos = [...new Set(detalles.map((d) => d.tipo))];

  return tipos.map((t) => {

    if (t === 'caja') return 'Caja';

    if (t === 'cuenta') return 'Cuenta';

    if (t === 'cheque_tercero') return 'Cheque de tercero';

    return t;

  }).join(', ');

}



function detalleFondoResumen(mf) {

  const det = mf.detalles || [];

  if (det.length === 0) return '-';

  if (det.length === 1) {

    const d = det[0];

    const pref = d.tipo === 'caja'

      ? `Caja: ${d.caja_nombre || '—'}`

      : d.tipo === 'cuenta'

        ? 'Cuenta'

        : `Cheque ${d.cheque_numero || ''}`;

    return `${pref} — $${fmtNumExport(d.monto || 0)}`;

  }

  return `${det.length} ítems`;

}



export function valorCeldaFondosExport(mf, colId, formatFecha) {

  switch (colId) {

    case 'fecha':

      return formatFecha(mf.fecha);

    case 'cuenta_banco':

      return textoOrigenDestino(mf);

    case 'tipo':

      return mf.clase_label || etiquetaClaseFondo(mf.clase_movimiento, mf.tipo);

    case 'concepto':

      return mf.concepto || '-';

    case 'ingreso': {

      const credito = mf.credito != null

        ? parseFloat(mf.credito)

        : (mf.tipo === 'ingreso' ? parseFloat(mf.monto_total || 0) : 0);

      return credito > 0 ? fmtNumExport(credito) : '';

    }

    case 'egreso': {

      const debito = mf.debito != null

        ? parseFloat(mf.debito)

        : (mf.tipo === 'egreso' ? parseFloat(mf.monto_total || 0) : 0);

      return debito > 0 ? fmtNumExport(debito) : '';

    }

    case 'saldo':

      return fmtNumExport(mf.saldo_acumulado || 0);

    case 'medio_pago':

      return medioPagoFondoLabel(mf);

    case 'usuario':

      return mf.usuario_nombre || '-';

    case 'detalles':

      return detalleFondoResumen(mf);

    default:

      return '';

  }

}



export function valorCeldaMovimientoExport(mov, colId, formatFecha) {

  switch (colId) {

    case 'fecha':

      return formatFecha(mov.fecha);

    case 'entidad':

      return mov.razon_social || '';

    case 'concepto': {

      const acl = textoAclaracionMovimiento(mov);

      const base = mov.concepto || '';

      return acl && acl !== base ? `${base} (${acl})` : base;

    }

    case 'debito':

      return mov.tipo_movimiento === 'DEBITO' ? fmtNumExport(mov.monto) : '';

    case 'credito':

      return mov.tipo_movimiento === 'CREDITO' ? fmtNumExport(mov.monto) : '';

    case 'saldo':

      return fmtNumExport(mov.saldo_acumulado);

    default:

      return '';

  }

}



export const COLUMNAS_MOVIMIENTOS_CUENTA = [
  { id: 'fecha', label: 'Fecha' },
  { id: 'concepto', label: 'Concepto' },
  { id: 'debito', label: 'Débito' },
  { id: 'credito', label: 'Crédito' },
  { id: 'saldo', label: 'Saldo acumulado' },
];

export function valorCeldaClienteExport(cliente, colId) {
  switch (colId) {
    case 'cuit': return cliente.cuit || '';
    case 'razon_social': return cliente.razon_social || '';
    case 'estado': return cliente.activo ? 'Activo' : 'Inactivo';
    case 'tipo': return cliente.tipo_nombre || '';
    case 'grupo': return cliente.grupo_nombre || '';
    case 'abono_mensual': return cliente.abono_mensual != null ? fmtNumExport(cliente.abono_mensual) : '';
    case 'empleados': return cliente.cantidad_empleados != null ? String(cliente.cantidad_empleados) : '';
    case 'direccion': return cliente.direccion || '';
    case 'provincia': return cliente.provincia_nombre || '';
    case 'localidad': return cliente.localidad_nombre || '';
    case 'telefono': return cliente.telefono || '';
    case 'condicion_impositiva': return cliente.condicion_impositiva || '';
    case 'ingresos_brutos': return cliente.ingresos_brutos || '';
    case 'drg': return cliente.drg || '';
    case 'dv': return cliente.dv || '';
    case 'iva_liquida':
      if (cliente.iva_liquida === 1 || cliente.iva_liquida === '1') return 'Sí';
      if (cliente.iva_liquida === 0 || cliente.iva_liquida === '0') return 'No';
      return '';
    case 'saldo': return fmtNumExport(cliente.saldo);
    default: return '';
  }
}

export function valorCeldaProveedorExport(proveedor, colId) {
  switch (colId) {
    case 'cuit': return proveedor.cuit || '';
    case 'razon_social': return proveedor.razon_social || '';
    case 'estado': return proveedor.activo ? 'Activo' : 'Inactivo';
    case 'tipo': return proveedor.tipo_nombre || '';
    case 'direccion': return proveedor.direccion || '';
    case 'provincia': return proveedor.provincia_nombre || '';
    case 'localidad': return proveedor.localidad_nombre || '';
    case 'telefono': return proveedor.telefono || '';
    case 'saldo': return fmtNumExport(proveedor.saldo);
    default: return '';
  }
}

export function valorCeldaEmpleadoExport(emp, colId, formatFecha) {
  const fmt = formatFecha || ((f) => (f ? formatFechaSistema(f) : ''));
  switch (colId) {
    case 'nombre': return emp.nombre || '';
    case 'telefono': return emp.telefono || '';
    case 'fecha_nacimiento': return fmt(emp.fecha_nacimiento);
    case 'sueldo': return emp.sueldo != null && emp.sueldo !== '' ? fmtNumExport(emp.sueldo) : '';
    case 'estado': return emp.activo ? 'Activo' : 'Inactivo';
    default: return '';
  }
}

export function imprimirListadoColumnas({
  titulo,
  subtitulo = '',
  columnDefs,
  visibleColumnIds,
  items,
  getCellValue,
  logoUrl,
}) {
  const logo = logoUrl || getPrintLogoUrl();
  const headers = visibleColumnIds.map((id) => columnDefs.find((c) => c.id === id)?.label || id);
  const rows = (items || []).map((item) => visibleColumnIds.map((id) => {
    const v = getCellValue(item, id);
    return String(v ?? '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }));
  const headerHtml = headers.map((h) => `<th>${h}</th>`).join('');
  const bodyHtml = rows.map((row) => `<tr>${row.map((c) => `<td>${c}</td>`).join('')}</tr>`).join('');
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>${titulo}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; color: #333; }
        ${estilosPrintHeader}
        .subtitulo { color: #666; margin-bottom: 16px; font-size: 11px; }
        table { width: 100%; border-collapse: collapse; margin-top: 8px; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; font-size: 11px; }
        th { background: #2c3e50; color: white; }
        @media print { body { padding: 10px; } }
      </style>
    </head>
    <body>
      ${buildPrintHeaderHtml({ titulo, logoUrl: logo, encabezado: getPrintEncabezado() })}
      ${subtitulo ? `<div class="subtitulo">${subtitulo}</div>` : ''}
      <table>
        <thead><tr>${headerHtml}</tr></thead>
        <tbody>${bodyHtml || `<tr><td colspan="${headers.length}">Sin registros</td></tr>`}</tbody>
      </table>
      <script>window.onload = function() { window.print(); };</script>
    </body>
    </html>
  `;
  const ventana = window.open('', '_blank');
  if (!ventana) throw new Error('No se pudo abrir la ventana de impresión');
  ventana.document.write(html);
  ventana.document.close();
}

function escHtml(s) {
  return String(s ?? '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function tablaHtml(headers, rows) {
  const th = headers.map((h) => `<th>${escHtml(h)}</th>`).join('');
  const body = rows.length
    ? rows.map((row) => `<tr>${row.map((c) => `<td>${escHtml(c)}</td>`).join('')}</tr>`).join('')
    : `<tr><td colspan="${headers.length}">Sin registros</td></tr>`;
  return `<table><thead><tr>${th}</tr></thead><tbody>${body}</tbody></table>`;
}

/**
 * Impresión de informe de cierre de caja (arqueo + cheques + diferencias).
 */
export function imprimirCierreCaja({
  titulo,
  subtitulo = '',
  resumen = [],
  efectivo = [],
  cheques = [],
  diferencias = null,
  ajuste = null,
  observaciones = '',
  logoUrl,
}) {
  const logo = logoUrl || getPrintLogoUrl();
  const resumenHtml = resumen.length
    ? `<div class="resumen-grid">${resumen.map((r) => `<div><strong>${escHtml(r.label)}:</strong> ${escHtml(r.value)}</div>`).join('')}</div>`
    : '';

  const filasEf = efectivo.map((d) => [
    d.nombre,
    d.cantidad,
    typeof d.subtotal === 'number' ? formatMoneyAr(d.subtotal) : d.subtotal,
  ]);
  const filasCh = cheques.map((ch) => [
    ch.numero || '—',
    ch.cliente_razon_social || ch.cliente || '—',
    typeof ch.importe === 'number' ? formatMoneyAr(ch.importe) : ch.importe,
    ch.marcado_fisico === false || ch.marcado === false ? 'No' : 'Sí',
  ]);

  let difHtml = '';
  if (diferencias) {
    difHtml = `
      <h2>Diferencias</h2>
      <div class="resumen-grid">
        <div><strong>Efectivo:</strong> ${escHtml(formatMoneyAr(diferencias.efectivo))}</div>
        <div><strong>Cheques:</strong> ${escHtml(formatMoneyAr(diferencias.cheques))}</div>
        <div><strong>Total:</strong> ${escHtml(formatMoneyAr(diferencias.total))}</div>
      </div>`;
  }

  let ajusteHtml = '';
  if (ajuste) {
    const tipoLabel = ajuste.tipo === 'ingreso' ? 'Ingreso' : 'Egreso';
    ajusteHtml = `
      <h2>Ajuste en fondos</h2>
      <p>Movimiento #${escHtml(String(ajuste.id))} — ${tipoLabel} de ${escHtml(formatMoneyAr(ajuste.monto))}</p>
      ${ajuste.concepto ? `<p style="color:#555;margin-top:4px">${escHtml(ajuste.concepto)}</p>` : ''}`;
  }

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>${escHtml(titulo)}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; color: #333; }
        ${estilosPrintHeader}
        .subtitulo { color: #666; margin-bottom: 16px; font-size: 11px; }
        h2 { font-size: 14px; margin: 20px 0 8px; color: #2c3e50; }
        .resumen-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 8px; margin-bottom: 12px; }
        table { width: 100%; border-collapse: collapse; margin-top: 4px; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; font-size: 11px; }
        th { background: #2c3e50; color: white; }
        .obs { margin-top: 16px; font-style: italic; color: #555; }
        @media print { body { padding: 10px; } }
      </style>
    </head>
    <body>
      ${buildPrintHeaderHtml({ titulo, logoUrl: logo, encabezado: getPrintEncabezado() })}
      ${subtitulo ? `<div class="subtitulo">${escHtml(subtitulo)}</div>` : ''}
      ${resumenHtml}
      <h2>Arqueo de efectivo</h2>
      ${tablaHtml(['Denominación', 'Cantidad', 'Subtotal'], filasEf)}
      <h2>Cheques en cartera</h2>
      ${tablaHtml(['N° cheque', 'Cliente', 'Importe', 'Marcado físico'], filasCh)}
      ${difHtml}
      ${ajusteHtml}
      ${observaciones ? `<p class="obs"><strong>Observaciones:</strong> ${escHtml(observaciones)}</p>` : ''}
      <script>window.onload = function() { window.print(); };</script>
    </body>
    </html>
  `;
  const ventana = window.open('', '_blank');
  if (!ventana) throw new Error('No se pudo abrir la ventana de impresión');
  ventana.document.write(html);
  ventana.document.close();
}

function formatMoneyAr(n) {
  return `$${(parseFloat(n) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
